import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Inbox, MessageCircle, CheckSquare, PhoneForwarded,
  PhoneMissed, Users, Reply, ChevronRight, Loader2, Phone, AlertCircle,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';

// ── Item metadata: icon, accent tone, friendly title, group ───────────
const ITEM_META = {
  recently_responded: { icon: Reply, tone: 'sky', title: 'Patient replies', group: 'communication' },
  open_tasks: { icon: CheckSquare, tone: 'amber', title: 'Open tasks', group: 'work' },
  open_followups: { icon: AlertCircle, tone: 'rose', title: 'Call follow-ups', group: 'work' },
  open_winbacks: { icon: PhoneForwarded, tone: 'violet', title: 'Win-back opportunities', group: 'work' },
  missed_calls: { icon: PhoneMissed, tone: 'rose', title: 'Missed calls', group: 'work' },
  open_leads: { icon: Users, tone: 'teal', title: 'Open leads', group: 'pipeline' },
};

const GROUPS = [
  {
    id: 'communication',
    label: 'Patient Communication',
    accent: 'text-sky-600',
    accentDot: 'bg-sky-400',
    description: 'Conversations & replies needing attention',
  },
  {
    id: 'work',
    label: 'Open Work',
    accent: 'text-amber-600',
    accentDot: 'bg-amber-400',
    description: 'Tasks, follow-ups, win-backs, missed calls',
  },
  {
    id: 'pipeline',
    label: 'Pipeline',
    accent: 'text-teal-600',
    accentDot: 'bg-teal-400',
    description: 'Active leads in the funnel',
  },
];

const TONES = {
  rose: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200', soft: 'bg-rose-500/10', badge: 'bg-rose-500 text-white' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', soft: 'bg-amber-500/10', badge: 'bg-amber-500 text-white' },
  sky: { bg: 'bg-sky-50', text: 'text-sky-600', border: 'border-sky-200', soft: 'bg-sky-500/10', badge: 'bg-sky-500 text-white' },
  violet: { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200', soft: 'bg-violet-500/10', badge: 'bg-violet-500 text-white' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200', soft: 'bg-teal-500/10', badge: 'bg-teal-500 text-white' },
  slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', soft: 'bg-slate-500/10', badge: 'bg-slate-700 text-white' },
};

/**
 * Daily Pulse — left-side sheet showing the team's open work for the day.
 * Three editorial groups: Patient Communication, Open Work, Pipeline.
 */
export function DailyPulseSheet({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user, currentLocation } = useAuth();
  const [notifications, setNotifications] = useState({ items: [], total: 0 });
  const [unreadInbox, setUnreadInbox] = useState(0);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState('clinic'); // 'clinic' | 'me'

  const load = useCallback(async () => {
    if (!currentLocation?.id) return;
    setLoading(true);
    try {
      const params = { location_id: currentLocation.id };
      if (scope === 'me' && user?.name) {
        params.assigned_to = user.name;
      }
      const [notifResp, inboxResp] = await Promise.all([
        api.get('/auth/notifications', { params }),
        api.get('/inbox/unread-count', { params: { location_id: currentLocation.id } }).catch(() => ({ data: { unread: 0 } })),
      ]);
      setNotifications(notifResp.data || { items: [], total: 0 });
      setUnreadInbox(inboxResp.data?.unread || 0);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [currentLocation?.id, scope, user?.name]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Merge: server items + the synthetic "unread_inbox" tile (sourced from /inbox/unread-count)
  const allItems = useMemo(() => {
    const merged = [...(notifications.items || [])];
    if (unreadInbox > 0) {
      merged.unshift({
        type: 'unread_inbox',
        count: unreadInbox,
        label: `${unreadInbox} unread inbox message${unreadInbox === 1 ? '' : 's'}`,
        link: '/inbox',
        priority: 'high',
        group: 'communication',
      });
    }
    return merged;
  }, [notifications.items, unreadInbox]);

  const totalOpen = allItems.reduce((s, i) => s + (i.count || 0), 0);

  const grouped = useMemo(() => {
    const map = { communication: [], work: [], pipeline: [] };
    for (const item of allItems) {
      const g = item.group || ITEM_META[item.type]?.group || 'work';
      if (map[g]) map[g].push(item);
    }
    return map;
  }, [allItems]);

  const today = new Date();
  const dateLabel = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const firstName = user?.name?.split(' ')[0] || 'you';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-full sm:max-w-md p-0 overflow-hidden flex flex-col bg-slate-50"
        data-testid="daily-pulse-sheet"
      >
        {/* ── Editorial dark header ────────────────────────────────── */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-6 pt-7 pb-6 text-white">
          {/* Subtle radial glow */}
          <div
            aria-hidden
            className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-teal-500/10 blur-3xl"
          />
          <div
            aria-hidden
            className="absolute -bottom-32 -left-24 h-64 w-64 rounded-full bg-violet-500/10 blur-3xl"
          />

          <SheetHeader className="relative space-y-1 text-left">
            <p className="text-[9.5px] font-bold uppercase tracking-[0.22em] text-teal-300 flex items-center gap-1.5">
              <Sparkles size={10} className="text-teal-400" />
              Daily Pulse
            </p>
            <SheetTitle className="text-white text-[22px] font-bold tracking-tight">
              {dateLabel}
            </SheetTitle>
            <SheetDescription className="text-slate-400 text-[12.5px] mt-1.5">
              {scope === 'clinic'
                ? `Live snapshot across ${currentLocation?.name || 'this location'}.`
                : `Items in your queue, ${firstName}.`}
            </SheetDescription>
          </SheetHeader>

          {/* Scope toggle */}
          <div className="relative mt-4 inline-flex items-center gap-0.5 rounded-full bg-slate-800/70 backdrop-blur-sm border border-slate-700/80 p-0.5" data-testid="pulse-scope-toggle">
            <button
              type="button"
              onClick={() => setScope('clinic')}
              className={cn(
                'px-3.5 py-1 text-[11px] font-semibold rounded-full transition-all',
                scope === 'clinic'
                  ? 'bg-teal-400 text-slate-950 shadow-[0_0_0_1px_rgba(20,184,166,0.4),0_4px_12px_-2px_rgba(20,184,166,0.4)]'
                  : 'text-slate-300 hover:text-white'
              )}
              data-testid="pulse-scope-clinic"
            >
              Clinic
            </button>
            <button
              type="button"
              onClick={() => setScope('me')}
              className={cn(
                'px-3.5 py-1 text-[11px] font-semibold rounded-full transition-all',
                scope === 'me'
                  ? 'bg-teal-400 text-slate-950 shadow-[0_0_0_1px_rgba(20,184,166,0.4),0_4px_12px_-2px_rgba(20,184,166,0.4)]'
                  : 'text-slate-300 hover:text-white'
              )}
              data-testid="pulse-scope-me"
            >
              Your Assigned Tasks
            </button>
          </div>

          {/* Headline number */}
          {!loading && (
            <div className="relative mt-5 flex items-end gap-3">
              <span className="text-[44px] font-black tabular-nums leading-none text-white tracking-tighter">
                {totalOpen}
              </span>
              <span className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-400 pb-1.5">
                Open item{totalOpen === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </div>

        {/* ── Body: grouped sections ──────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Loader2 size={26} className="animate-spin mb-2 text-teal-500" />
              <p className="text-xs font-medium">Loading your day…</p>
            </div>
          ) : totalOpen === 0 ? (
            <EmptyAllClear scope={scope} />
          ) : (
            GROUPS.map(group => {
              const groupItems = grouped[group.id] || [];
              if (groupItems.length === 0) return null;
              const groupTotal = groupItems.reduce((s, i) => s + (i.count || 0), 0);
              return (
                <section key={group.id} data-testid={`pulse-group-${group.id}`}>
                  <div className="flex items-center justify-between mb-2 px-0.5">
                    <div className="flex items-center gap-2">
                      <span className={cn('h-1.5 w-1.5 rounded-full', group.accentDot)} aria-hidden />
                      <h3 className={cn('text-[10px] font-bold uppercase tracking-[0.18em]', group.accent)}>
                        {group.label}
                      </h3>
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 tabular-nums">
                      {groupTotal}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {groupItems.map(item => (
                      <PulseItem
                        key={item.type}
                        item={item}
                        onClick={() => { onOpenChange(false); navigate(item.link); }}
                      />
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>

        {/* Footer hint */}
        {!loading && totalOpen > 0 && (
          <div className="px-5 pb-4 pt-2 border-t border-slate-200 bg-white/60">
            <p className="text-[10.5px] text-slate-400 text-center">
              Tap any tile to jump straight to that module.
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Single pulse tile ──────────────────────────────────────────────
function PulseItem({ item, onClick }) {
  const meta = ITEM_META[item.type] || { icon: Inbox, tone: 'slate', title: item.label };
  if (item.type === 'unread_inbox') {
    meta.icon = MessageCircle;
    meta.tone = 'rose';
  }
  const tone = TONES[meta.tone] || TONES.slate;
  const Icon = meta.icon;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`pulse-item-${item.type}`}
      className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-[0_4px_14px_-6px_rgba(15,23,42,0.18)] hover:-translate-y-[1px] active:translate-y-0 transition-all text-left"
    >
      <div className={cn(
        'relative w-10 h-10 rounded-lg flex items-center justify-center border shrink-0 transition-transform group-hover:scale-105',
        tone.bg, tone.border
      )}>
        <Icon size={16} strokeWidth={2.1} className={tone.text} />
        {item.priority === 'high' && (
          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-white" aria-hidden />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-semibold text-slate-800 truncate leading-tight">
          {item.label}
        </p>
        <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wide font-bold">
          {item.priority === 'high' ? 'Urgent' : 'Open'} · click to view
        </p>
      </div>
      <span className={cn(
        'inline-flex items-center justify-center min-w-[26px] h-[26px] px-1.5 rounded-md text-[11px] font-bold tabular-nums shadow-sm',
        tone.badge
      )}>
        {item.count}
      </span>
      <ChevronRight size={14} className="text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}

// ── Empty state ────────────────────────────────────────────────────
function EmptyAllClear({ scope }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="pulse-all-clear">
      <div className="relative h-16 w-16 rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center mb-3 ring-4 ring-emerald-50">
        <Sparkles size={26} className="text-emerald-500" />
        <span className="absolute inset-0 rounded-full animate-ping bg-emerald-400/20" aria-hidden />
      </div>
      <p className="text-[15px] font-bold text-slate-800 tracking-tight">All clear.</p>
      <p className="text-[12.5px] text-slate-500 mt-1 max-w-[240px]">
        {scope === 'me'
          ? "Nothing personal pending — perfect time to make a difference."
          : "The whole team is caught up. Beautiful."}
      </p>
    </div>
  );
}

export default DailyPulseSheet;
